import { atomWithKey as atom } from "./atomWithKey";
import { atom as jotaiAtom } from "jotai";
import { IOfferPassType, Offer } from "../Types/Types/OffersResponse";
import { AvailableDate, OffersRequest, PassengerPriceDetails, PaymentItemDetails, ISelectedPassenger, IPassengerSpecification, IOffer, IExchangeOfferResponse, ILoyaltyTier, ILoyaltyProgramResponse } from "../Types/Index";
import { TrainType } from "../Types/Types/TrainTypes";
import { DefaultEnum, ServiceClassTypeEnum, TrainTypeEnum } from "../Types/Enums/globalDefaultEnum";
import { IPaymentItemDetails } from "../Types/Interface/IPayment";
import { ITiersConfig } from "../Types/Interface/ICustomerTiers";
import { ICmsMonthlyPassOffer, ICmsTravelPassOffer } from "../Types/Interface/ICMSOffers";
import { IPassOffer, IPassOfferResponse } from "../../app/api/getPassOffers";
import { IPassForm } from "../../app/offers/OfferPassLeftSection";
import { IPassBooking } from "../Types/Types/BookingTypes";
import { searchStateAtom } from "./atoms";
import { determinePassengerTypeFromPassenger } from "../../Lib/Utils/validationUtil";
import { getPassBookingSummary } from "../../Actions/LogicActions/CMSOffers/GetPassBookingSummary";
import { getRecoilStateValue, TAtomList } from "./Events/Recoil";
import dayjs from 'dayjs';

export const testStringAtom = atom<string>({
  key: "testStringAtom",
  default: "",
});

export type OffersPageView = "listing" | "offerDetail" | "bookedPassDetail";

export const offersPageViewAtom = atom<OffersPageView>({
  key: "offersPageViewAtom",
  default: "listing",
});

export const offerAtom = atom<Offer[]>({
  key: 'offerAtom',
  default: [],
});

export const openSearchModalAtom = atom<boolean>({
  key: "openSearchModalAtom",
  default: false,
});

export const trainTypeAtom = atom<TrainTypeEnum>({
  key: "trainTypeAtom",
  default: TrainTypeEnum.outbound,
});

export const availableDatesAtom = atom<AvailableDate[]>({
  key: "availableDatesAtom",
  default: [],
});

export const offersRequestAtom = atom<OffersRequest | null>({
  key: "offersRequestAtom",
  default: null,
});

export const filterStateAtom = atom({
  key: "filterStateAtom",
  default: [],
});

export const selectedServiceClassAtom = atom<typeof ServiceClassTypeEnum[keyof typeof ServiceClassTypeEnum]>({
  key: "selectedServiceClassAtom",
  default: ServiceClassTypeEnum.STANDARD,
});


const initialTrainData: TrainType[] = [];
export const trainAtom = atom<TrainType[]>({
  key: 'trainAtom',
  default: initialTrainData,
});

export const startIndexDateSelectorAtom = atom<number>({
  key: "startIndexDateSelectorAtom",
  default: 0
});

export const filterTrainsAtom = atom<TrainType[]>({
  key: 'filterTrainsAtom',
  default: initialTrainData,
});

export const visibleTrainsAtom = atom<number>({
  key: "visibleTrainsAtom",
  default: DefaultEnum.defaultVisibleTrain
});

export const selectedOutboundTrainAtom = atom<TrainType | null>({
  key: "selectedOutboundTrainAtom",
  default: null,
});

export const selectedInboundTrainAtom = atom<TrainType | null>({
  key: "selectedInboundTrainAtom",
  default: null,
});

export const selectedOfferIdAtom = atom<string | string[] | null>({
  key: "selectedOfferIdAtom",
  default: null,
});

export const selectedShuttleOfferIdAtom = atom<string | string[] | null>({
  key: "selectedShuttleOfferIdAtom",
  default: null,
});

export const outboundShuttleOfferIdAtom = atom<string | string[] | null>({
  key: "outboundShuttleOfferIdAtom",
  default: null,
});

export interface IOriginalTrainClassSelection {
  outbound: "standardValue" | "premium";
  inbound: "standardValue" | "premium";
}

export const originalTrainClassSelectionAtom = atom<IOriginalTrainClassSelection | null>({
  key: "originalTrainClassSelectionAtom",
  default: null,
});

export const ticketTypeStateAtom = atom<string>({
  key: "ticketTypeStateAtom",
  default: "",
});

export const passengerSpecificationsTypesAtom = atom<IPassengerSpecification[]>({
  key: 'passengerSpecificationsTypesAtom',
  default: [],
});

//Payment Related Atoms
export const paymentOutboundAtom = atom<PaymentItemDetails[]>({
  key: "paymentOutboundAtom",
  default: [],
});

export const paymentOutboundRefundAtom = atom<PaymentItemDetails[]>({
  key: "paymentOutboundRefundAtom",
  default: [],
});

export const paymentInboundAtom = atom<PaymentItemDetails[]>({
  key: "paymentInboundAtom",
  default: [],
});

export const paymentInboundRefundAtom = atom<PaymentItemDetails[]>({
  key: "paymentInboundRefundAtom",
  default: [],
});
export const selectedTrainStateAtom = atom<TrainType | null>({
  key: "selectedTrainStateAtom",
  default: null,
});

export const getSearchOfferAtom = jotaiAtom((get) => {
    const offers = get(offerAtom);
    return {
      offers
    };
});
getSearchOfferAtom.debugLabel = "getSearchOfferAtom";
export const selectedDateAtom = atom<{
  [key: number]: string | null;
}>({
  key: "selectedDateAtom",
  default: "",
});
export const selectedBookingIdForCancelAtom = atom<string>({
  key: "selectedBookingIdForCancelAtom",
  default: "",
});
export const cancelBookingPreviousRouteAtom = atom<string>({
  key: "cancelBookingPreviousRouteAtom",
  default: "",
});
export const upgradePreviousRouteAtom = atom<string>({
  key: "upgradePreviousRouteAtom",
  default: "",
});
export const allPassengerPriceAndAgeGroupAtom = atom<PassengerPriceDetails[]>({
  key: "allPassengerPriceAndAgeGroupAtom",
  default: [],
});
export const allPassengerPriceAndAgeGroupInBoundAtom = atom<PassengerPriceDetails[]>({
  key: "allPassengerPriceAndAgeGroupInBoundAtom",
  default: [],
});
export const tripDisableForCancelSelectionAtom = atom<string[]>({
  key: "tripDisableForCancelSelectionAtom",
  default: [],
});
export const selectedBookingTripStatusAtom = atom<string>({
  key: "selectedBookingTripStatusAtom",
  default: "",
});
export const selectedPassengersOutBoundAtom = atom<ISelectedPassenger[]>({
  key: "selectedPassengersOutBoundAtom",
  default: [],
});
export const selectedPassengersInBoundAtom = atom<ISelectedPassenger[]>({
  key: "selectedPassengersInBoundAtom",
  default: [],
});
export const paymentOutBoundItemsDetailsAtom = atom<IPaymentItemDetails[]>({
  key: "paymentOutBoundItemsDetailsAtom",
  default: [],
});
export const paymentInboundItemsDetailsAtom = atom<IPaymentItemDetails[]>({
  key: "paymentInboundItemsDetailsAtom",
  default: [],
});

export const alreadyPaidOutBoundAtom = atom<number>({
  key: "alreadyPaidOutBoundAtom",
  default: 0,
});

export const alreadyPaidInBoundAtom = atom<number>({
  key: "alreadyPaidInBoundAtom",
  default: 0,
});

export const exchangeOffersAtom = atom<string[]>({
  key: "exchangeOffersAtom",
  default: [],
});

export const selectedPassengerForDateChangeAtom = atom<ISelectedPassenger[]>({
  key: "selectedPassengerForDateChangeAtom",
  default: [],
});

export const selectedPassengerForDateChangeInBoundAtom = atom<ISelectedPassenger[]>({
  key: "selectedPassengerForDateChangeInBoundAtom",
  default: [],
});

export const selectedBookingIdForDateChangeAtom = atom<string>({
  key: "selectedBookingIdForDateChangeAtom",
  default: "",
});

export const selectedBookingIdForBookReturnJourneyAtom = atom<string>({
  key: "selectedBookingIdForBookReturnJourneyAtom",
  default: "",
});

export const tripDisableForDateChangeSelectionAtom = atom<string[]>({
  key: "tripDisableForDateChangeSelectionAtom",
  default: [],
});

export const getLowestTripPriceSelector = jotaiAtom((get): number | null => {
    const trains = get(trainAtom);

    let allTripsMinimum = trains.map((train) => {
      let trainPrices = Object.values(train.priceInfo)
        .filter((i): i is { isSoldOut: boolean; price: number } => !Array.isArray(i) && i.isSoldOut === false)
        .map((i) => i.price);
      if (trainPrices.length === 0) return null;
      return Math.min.apply(null, trainPrices) || null;
    })
      .filter((i) => i !== null);

    return allTripsMinimum.length > 0 ? Math.min.apply(null, allTripsMinimum) : null;
});
getLowestTripPriceSelector.debugLabel = "getLowestTripPriceSelector";

export const initialTrainClassSelectionAtom = atom<IOriginalTrainClassSelection | null>({
  key: "initialTrainClassSelectionAtom",
  default: null,
});

// Upgrade to Premium per-leg offers
export const upgradeOutboundOffersAtom = atom<IExchangeOfferResponse[]>({
  key: "upgradeOutboundOffersAtom",
  default: [],
});

export const upgradeInboundOffersAtom = atom<IExchangeOfferResponse[]>({
  key: "upgradeInboundOffersAtom",
  default: [],
});

export const offerPassTypeAtom = atom<IOfferPassType | null>({
  key: "offerPassTypeAtom",
  default: null,
});

export const selectedCmsPassOfferAtom = atom<ICmsMonthlyPassOffer | ICmsTravelPassOffer | null>({
  key: "selectedCmsPassOfferAtom",
  default: null,
});

export const passOfferResponseAtom = atom<IPassOfferResponse | null>({
  key: "passOfferResponseAtom",
  default: null,
});

export const selectedPassOfferAtom = atom<IPassOffer | null>({
  key: "selectedPassOfferAtom",
  default: null,
});

export const passFormValuesAtom = atom<IPassForm | null>({
  key: "passFormValuesAtom",
  default: null
});

export const passBookingsAtom = atom<IPassBooking[]>({
  key: "passBookingsAtom",
  default: [],
});

export const selectedPassBookingAtom = atom<IPassBooking | null>({
  key: "selectedPassBookingAtom",
  default: null,
});
export const earnPointsAtom = atom<number>({
  key: "earnPointsAtom",
  default: 0,
});

export const earnExtraPointsAtom = atom<number>({
  key: "earnExtraPointsAtom",
  default: 0,
});

export const earnPointsTotalAtom = atom<number>({
  key: "earnPointsTotalAtom",
  default: 0,
});

export const earnPointsOutboundAtom = atom<number>({
  key: "earnPointsOutboundAtom",
  default: 0,
});

export const earnPointsInboundAtom = atom<number>({
  key: "earnPointsInboundAtom",
  default: 0,
});

export const loyaltyTiersAtom = atom<ILoyaltyTier[]>({
  key: "loyaltyTiersAtom",
  default: [],
});

export const loyaltyProgramResponseAtom = atom<ILoyaltyProgramResponse | null>({
  key: "loyaltyProgramResponseAtom",
  default: null,
});

export const tiersConfigAtom = atom<ITiersConfig | null>({
  key: "tiersConfigAtom",
  default: null,
});

export const customerLoyaltyTierRateAtom = atom<number>({
  key: "customerLoyaltyTierRateAtom",
  default: 1,
});


export const availableBookingPassOffersAtom = jotaiAtom((get) => {
    const passBooking = get(passBookingsAtom);
    const searchState = get<any>(searchStateAtom);

    // Determine currently selected travel date based on active train type
    const currentTrainType = getRecoilStateValue<TrainTypeEnum>("trainTypeAtom");
    const selectedTravelDate =
      currentTrainType === TrainTypeEnum.outbound
        ? searchState?.outboundDate
        : searchState?.inboundDate;

    const travelDate = selectedTravelDate ? dayjs(selectedTravelDate) : null;

    const availablePassOffers = passBooking.filter((passBooking) => {
      const passengerType = determinePassengerTypeFromPassenger(passBooking.booking.booking.passengers[0]);

      const isValidPassengerType = (passengerType in searchState?.passengers && searchState.passengers[passengerType as keyof typeof searchState.passengers] > 0);
      if (!isValidPassengerType) return false;

      const passOrigin = passBooking.travelAccount.travelAccount.regionalConstraint?.regionalValidities[0].zone.entryStation.ref.stopPlaceRef;
      const passDestination = passBooking.travelAccount.travelAccount.regionalConstraint?.regionalValidities[0].zone.terminalStation.ref.stopPlaceRef;

      let isStationMatch = searchState.from === passOrigin && searchState.to === passDestination;
      if (currentTrainType === TrainTypeEnum.inbound) {
        isStationMatch = searchState.to === passOrigin && searchState.from === passDestination;
      } else {
        isStationMatch = searchState.from === passOrigin && searchState.to === passDestination;
      }

      const passSummary = getPassBookingSummary(passBooking);
      const isPassExpired = passSummary?.summary.duration.remaining <= 0;
      if (isPassExpired) return false;

      const validFrom = dayjs(passBooking.travelAccount.travelAccount.validFrom);
      const validUntil = dayjs(passBooking.travelAccount.travelAccount.validUntil);

      // If we don't have a selected travel date yet, keep existing date-window validation
      if (!travelDate) {
        const currentDate = dayjs();
        if (!validFrom.isBefore(currentDate) || !validUntil.isAfter(currentDate)) {
          return false;
        }
        return isValidPassengerType && isStationMatch && !isPassExpired;
      }

      // For a specific selected travel date, the pass must be valid on that day
      const isBeforeValidFrom = travelDate.isBefore(validFrom, "day");
      const isAfterValidUntil = travelDate.isAfter(validUntil, "day");
      if (isBeforeValidFrom || isAfterValidUntil) {
        return false;
      }

      return isValidPassengerType && isStationMatch && !isPassExpired;

    });
    return availablePassOffers;
});
availableBookingPassOffersAtom.debugLabel = "availableBookingPassOffersAtom";


export const groupedAvailableBookingPassOffersAtom = jotaiAtom((get) => {
    const availablePassOffers = get(availableBookingPassOffersAtom);

    return availablePassOffers.reduce((groups: { [key: string]: IPassBooking[] }, passBooking) => {
      const passengerType = determinePassengerTypeFromPassenger(passBooking.booking.booking.passengers[0]);

      if (!groups[passengerType]) {
        groups[passengerType] = [];
      }
      groups[passengerType].push(passBooking);
      return groups;
    }, {});
});
groupedAvailableBookingPassOffersAtom.debugLabel = "groupedAvailableBookingPassOffersAtom";

export const PASS_BOOKING_WHITELIST_ATOMS_PREFIX = "passBookingAtom."
export const PASS_BOOKING_WHITELIST_ATOMS: TAtomList[] = [
  "offerPassTypeAtom",
  "passOfferResponseAtom",
  "passFormValuesAtom",
  "selectedPassOfferAtom",
  "passengerTypesStateAtom",
  "cmsAboutPassDetailAtom",
  "selectedCmsPassOfferAtom",
  "bookingResponseAtom"
]

export const generatePassBookingPersistAtomKey = (key: string) => {
  return PASS_BOOKING_WHITELIST_ATOMS_PREFIX + "_" + key;
}

export const isStandardPrivacySeatingCheckedAtom = atom<boolean>({
  key: "isStandardPrivacySeatingCheckedAtom",
  default: false,
});

export const isPremiumPrivacySeatingCheckedAtom = atom<boolean>({
  key: "isPremiumPrivacySeatingCheckedAtom",
  default: false,
});

export const selectedStandardPrivacySeatingOutboundAtom = atom<string | undefined>({
  key: "selectedStandardPrivacySeatingOutboundAtom",
  default: undefined,
});

export const selectedStandardPrivacySeatingInboundAtom = atom<string | undefined>({
  key: "selectedStandardPrivacySeatingInboundAtom",
  default: undefined,
});

export const selectedPremiumPrivacySeatingOutboundAtom = atom<string | undefined>({
  key: "selectedPremiumPrivacySeatingOutboundAtom",
  default: undefined,
});

export const selectedPremiumPrivacySeatingInboundAtom = atom<string | undefined>({
  key: "selectedPremiumPrivacySeatingInboundAtom",
  default: undefined,
});